import { Box, BoxProps } from '@chakra-ui/react'
import { useReducedMotion } from 'framer-motion'

interface CardProps extends BoxProps {
  children: React.ReactNode
  /**
   * Opt-in hover lift for cards that represent a clickable/interactive item.
   * Defaults to `false` — most `<Card>` usages wrap static/display content, and
   * unconditionally adding a hover affordance to all of them would visually
   * suggest they're clickable when they aren't.
   */
  interactive?: boolean
}

function Card({ children, interactive = false, ...rest }: CardProps) {
  // Reads the OS `prefers-reduced-motion` setting reactively. When set, skip the
  // transform-based lift (a non-motion boxShadow bump still communicates affordance).
  const prefersReducedMotion = useReducedMotion()

  const hoverStyles =
    interactive && !prefersReducedMotion
      ? { _hover: { boxShadow: 'md', transform: 'translateY(-2px)' }, transition: 'all 0.2s' }
      : interactive
        ? { _hover: { boxShadow: 'md' } }
        : {}

  return (
    <Box
      bg="surface"
      shadow="sm"
      rounded="xl"
      borderWidth="1px"
      borderColor="border"
      overflow="hidden"
      minW={0}
      {...hoverStyles}
      {...rest}
    >
      {children}
    </Box>
  )
}

// Plain Box, not Chakra's built-in CardHeader/CardBody/CardFooter — those read from a
// style-context only Chakra's own Card component provides, which this custom Card doesn't.
function CardHeader({ children, ...rest }: BoxProps) {
  return (
    <Box
      px={{ base: 4, md: 6 }}
      pt={{ base: 4, md: 6 }}
      pb={3}
      borderBottomWidth="1px"
      borderColor="border"
      {...rest}
    >
      {children}
    </Box>
  )
}

function CardBody({ children, ...rest }: BoxProps) {
  return (
    <Box px={{ base: 4, md: 6 }} py={{ base: 4, md: 6 }} {...rest}>
      {children}
    </Box>
  )
}

function CardFooter({ children, ...rest }: BoxProps) {
  return (
    <Box
      px={{ base: 4, md: 6 }}
      pb={{ base: 4, md: 6 }}
      pt={3}
      borderTopWidth="1px"
      borderColor="border"
      {...rest}
    >
      {children}
    </Box>
  )
}

Card.Header = CardHeader
Card.Body = CardBody
Card.Footer = CardFooter

export default Card
